import React, { useEffect, useState } from 'react';
import { Loader2, Target, Gauge, AlertTriangle, CircleDot } from 'lucide-react';
import MotoShell from '../components/MotoShell';
import { api } from '../lib/api';
import { MOTO_PLATEAU } from '../data/motoUnivers';

const CAT_META = {
  'maitrise-statique': { label: 'Maîtrise à allure réduite', icon: CircleDot, cls: 'text-sky-300 border-sky-500/30 bg-sky-500/[0.06]' },
  dynamique: { label: 'Exercices dynamiques', icon: Gauge, cls: 'text-violet-300 border-violet-500/30 bg-violet-500/[0.06]' },
  urgence: { label: 'Situations d\u2019urgence', icon: AlertTriangle, cls: 'text-amber-300 border-amber-500/30 bg-amber-500/[0.06]' },
};
const CAT_ORDER = ['maitrise-statique', 'dynamique', 'urgence'];

export default function MotoPlateauPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    document.title = 'Le plateau moto — 6 manœuvres officielles + 12 exercices | Code 2 Roues';
    api.motoPlateau().then(setData).catch(() => setData({ exercices: [], categories: [] }));
  }, []);

  return (
    <MotoShell>
      <section className="max-w-7xl mx-auto px-4 lg:px-6 pt-10 sm:pt-14" data-testid="moto-plateau-page">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-sky-400 font-bold font-mono-spec mb-2">Épreuve hors circulation · Officiel 2026</div>
        <h1 className="font-serif-editorial text-[30px] sm:text-5xl leading-[1.05] max-w-3xl">Le plateau, décortiqué.</h1>
        <p className="text-white/60 text-[13.5px] sm:text-[15px] mt-3 max-w-2xl leading-relaxed">
          Les 6 manœuvres du parcours officiel + les 12 exercices d&apos;entraînement travaillés en formation, avec leurs critères de réussite et les compétences REMC associées.
        </p>

        {/* 6 manœuvres officielles */}
        <div className="mt-8">
          <h2 className="font-serif-editorial text-[22px] sm:text-[28px]">Les 6 manœuvres de l&apos;examen</h2>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {MOTO_PLATEAU.map((m) => (
              <div key={m.n} data-testid={`plateau-manoeuvre-${m.n}`} className="rounded-xl border border-sky-500/25 bg-gradient-to-br from-[#081020] to-[#0b0f1a] p-4">
                <div className="flex items-center gap-2.5">
                  <span className="font-mono-spec text-sky-400 font-bold text-[15px]">{m.n}</span>
                  <span className="text-white/40 text-[10.5px] font-mono-spec uppercase tracking-widest">{m.spec}</span>
                </div>
                <div className="text-white font-serif-editorial text-[18px] mt-1.5">{m.titre}</div>
                <div className="text-white/50 text-[12px] mt-1">{m.detail}</div>
              </div>
            ))}
          </div>
        </div>

        {/* 12 exercices */}
        {!data ? (
          <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-sky-400" size={28} /></div>
        ) : (
          <div className="mt-12 space-y-10 mb-6">
            {CAT_ORDER.map((cat) => {
              const meta = CAT_META[cat];
              const exos = (data.exercices || []).filter((e) => e.categorie === cat);
              if (!exos.length) return null;
              const Icon = meta.icon;
              return (
                <div key={cat}>
                  <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-[11px] font-mono-spec uppercase tracking-widest font-bold ${meta.cls}`}>
                    <Icon size={13} /> {meta.label} · {exos.length} exercices
                  </div>
                  <div className="mt-4 grid md:grid-cols-2 gap-3">
                    {exos.map((e) => (
                      <div key={e.id} data-testid={`plateau-exo-${e.id}`} className="rounded-xl border border-white/10 bg-[#0b0f1a] p-4 sm:p-5">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="text-white/40 text-[10.5px] font-mono-spec uppercase tracking-widest">Exercice {e.id} · {e.allure}</div>
                            <div className="text-white font-serif-editorial text-[19px] mt-1">{e.titre}</div>
                            <div className="text-white/55 text-[12.5px] mt-1 leading-snug">{e.sousTitre}</div>
                          </div>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {(e.remcCodes || []).map((c) => (
                            <span key={c} className="px-2 py-0.5 rounded-full bg-sky-500/10 border border-sky-500/30 text-sky-300 text-[10.5px] font-mono-spec font-bold">{c}</span>
                          ))}
                        </div>
                        {(e.criteres || []).length > 0 && (
                          <div className="mt-3 rounded-lg border border-white/8 bg-white/[0.02] p-3">
                            <div className="text-white/45 text-[10px] font-mono-spec uppercase tracking-widest font-bold inline-flex items-center gap-1.5 mb-2"><Target size={11} /> Critères de réussite</div>
                            <ul className="space-y-1.5">
                              {e.criteres.map((c, i) => (
                                <li key={i} className="text-[12.5px] leading-snug flex gap-2">
                                  <span className="text-sky-400 shrink-0">▸</span>
                                  <span><strong className="text-white">{c.label}</strong> <span className="text-white/55">— {c.detail}</span></span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </MotoShell>
  );
}
